import { useMantineTheme } from "@mantine/core";
import { useModals } from "@mantine/modals";
import { useCallback, memo, useState } from "react";
import ActionCard from "src/components/ActionCard";
import ListActions, {
  IActiveOption,
  IListObj,
} from "src/components/ListActions";
import {
  GetMeetingQuery,
  useCreateWaitlisteeMutation,
  useDeleteWaitlisteeMutation,
  usePushToMeetingMutation,
} from "src/generated/graphql";
import { SearchList } from "../SearchList";
import { GetMeeting } from "src/api/graphql/queries/Meeting";
import { MeetingTypes } from "src/utils/constants";

type IWaitlist = {
  meetingDetail: GetMeetingQuery["meetingOne"];
  isAllowWrite: boolean;
};

function Waitlist({ meetingDetail, isAllowWrite }: IWaitlist) {
  const [activeOption, setActiveOption] = useState<IActiveOption>(undefined);
  const { waitlistees, attendances, maxOccupancy, waitlistMax } =
    meetingDetail || {};
  const modalId = "search-clients";
  const modals = useModals();
  const theme = useMantineTheme();
  let waitlisteesLen = waitlistees?.length;
  const refetchQuery = [
    { query: GetMeeting, variables: { id: meetingDetail!.id } },
  ];

  const [moveToAttendee, { loading: moveLoading }] = usePushToMeetingMutation({
    refetchQueries: refetchQuery,
  });

  const [createWaitlistee] = useCreateWaitlisteeMutation({
    onCompleted() {
      modals.closeModal(modalId);
    },
    refetchQueries: refetchQuery,
  });

  const [removeWaitlistee, { loading: deleteLoading }] =
    useDeleteWaitlisteeMutation({
      refetchQueries: refetchQuery,
    });

  const handleSubmit = (id: string) => {
    meetingDetail &&
      createWaitlistee({
        variables: {
          data: {
            client: { id },
            meeting: { id: meetingDetail.id },
            order: 1,
          },
        },
      });
  };

  const handleModal = useCallback(() => {
    let attendeesClients =
      attendances
        ?.filter((e) => e.attendee.client?.id)
        .map((c) => c.attendee.client!.id) || [];
    let _ids = waitlistees?.map((e) => e.client?.id) || [];
    modals.openModal({
      closeOnClickOutside: false,
      modalId,
      title: "Add Waitlistee",
      children: (
        <SearchList
          type={MeetingTypes.Waitlistee}
          onCancel={() => modals.closeModal(modalId)}
          onSubmit={(e) => handleSubmit(e)}
          currentIdList={[...attendeesClients, ..._ids]}
        />
      ),
    });
  }, [meetingDetail, modalId]);

  const onMoveFromWaitList = ({ clientId }: IListObj) => {
    clientId &&
      meetingDetail &&
      moveToAttendee({
        variables: { meetingId: meetingDetail.id, clientId },
      });
  };

  const onDelete = ({ id, name }: IListObj) => {
    modals.openContextModal("delete", {
      closeOnClickOutside: false,
      title: `Confirm Delete`,
      centered: true,
      innerProps: {
        innerText: `Are you sure you want to delete the selected option, ${name}?`,
        onDelete: () =>
          removeWaitlistee({
            variables: { id },
          }),
      },
    });
  };

  const minutesInDay = 24 * 60;
  const miliSeconds = 1000;
  const minutesInHour = 60;
  const currentTime = new Date().getTime();
  const meetingEndTime = new Date(meetingDetail?.endAt).getTime();
  const isDayDiff =
    new Date(currentTime) > new Date(meetingEndTime) &&
    Math.floor((currentTime - meetingEndTime) / miliSeconds / minutesInHour) >
      minutesInDay;

  return (
    <ActionCard
      mt={"xs"}
      title={`Waitlist - ${waitlisteesLen}/${waitlistMax} 
      ${waitlisteesLen === waitlistMax ? "- Full" : ""}`}
      handleModal={isAllowWrite ? handleModal : undefined}
      disabled={waitlisteesLen === waitlistMax || isDayDiff}
      hideChild={!waitlistees?.length}
    >
      <>
        <ListActions
          options={
            waitlistees?.map((e) => ({
              id: e.id,
              name: `${e.client.user.firstName} ${e.client.user.lastName}`,
              clientId: e.client?.id,
              checkForClient: true,
            })) || []
          }
          showEdit={attendances?.length !== maxOccupancy}
          isAttendee={false}
          waitlistLoading={moveLoading}
          cancelLoading={deleteLoading}
          onUpdateWaitList={onMoveFromWaitList}
          onCancel={onDelete}
          isAllowWrite={isAllowWrite}
          activeOption={activeOption}
          setActiveOption={setActiveOption}
        />
      </>
    </ActionCard>
  );
}

export default memo(Waitlist);
